/**
 * PAPPO — Theme Context Provider
 * Applies CSS variables to :root, persists in LocalStorage.
 * Provides useTheme() hook for components.
 */

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { themes, type ThemeName, type ThemeTokens } from '../themes/tokens';

interface ThemeContextType {
  themeName: ThemeName;
  theme: ThemeTokens;
  setTheme: (name: ThemeName) => void;
}

const ThemeContext = createContext<ThemeContextType>({
  themeName: 'light',
  theme: themes.light,
  setTheme: () => {},
});

const STORAGE_KEY = 'pappo_theme';

function applyThemeToDOM(theme: ThemeTokens) {
  const root = document.documentElement;
  root.setAttribute('data-theme', theme.name);

  const vars: Record<string, string> = {
    '--bg-primary': theme.bgPrimary,
    '--bg-secondary': theme.bgSecondary,
    '--bg-tertiary': theme.bgTertiary,
    '--text-primary': theme.textPrimary,
    '--text-secondary': theme.textSecondary,
    '--text-muted': theme.textMuted,
    '--accent-primary': theme.accentPrimary,
    '--accent-hover': theme.accentHover,
    '--accent-glow': theme.accentGlow,
    '--border-color': theme.borderColor,
    '--surface': theme.surface,
    '--sidebar-bg': theme.sidebarBg,
    '--navbar-bg': theme.navbarBg,
    '--msg-sent-bg': theme.msgSentBg,
    '--msg-sent-text': theme.msgSentText,
    '--msg-received-bg': theme.msgReceivedBg,
    '--msg-received-text': theme.msgReceivedText,
    '--shadow-sm': theme.shadowSm,
    '--shadow-md': theme.shadowMd,
    '--shadow-lg': theme.shadowLg,
    '--input-bg': theme.inputBg,
    '--card-bg': theme.cardBg,
    '--card-border': theme.cardBorder,
    '--scrollbar-thumb': theme.scrollbarThumb,
    '--selection-bg': theme.selectionBg,
    '--selection-text': theme.selectionText,
    '--auth-page-bg': theme.authPageBg,
    '--auth-dot': theme.authDot,
    '--auth-card-bg': theme.authCardBg,
    '--auth-card-border': theme.authCardBorder,
    '--auth-card-shadow': theme.authCardShadow,
    '--auth-logo-gradient': theme.authLogoGradient,
    '--auth-accent-text': theme.authAccentText,
    '--auth-link-bg': theme.authLinkBg,
    '--auth-link-border': theme.authLinkBorder,
    '--auth-glow': theme.authGlow,
    '--auth-link-hover-bg': theme.authLinkHoverBg,
    '--auth-link-hover-border': theme.authLinkHoverBorder,
  };

  for (const [prop, val] of Object.entries(vars)) {
    root.style.setProperty(prop, val);
  }
}

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const [themeName, setThemeName] = useState<ThemeName>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ThemeName | null;
    /* Default = Deep Space Cyber (preserva o visual hardcoded original das páginas). */
    return stored && themes[stored] ? stored : 'cyber';
  });

  const theme = themes[themeName];

  useEffect(() => {
    applyThemeToDOM(theme);
  }, [theme]);

  const setTheme = useCallback((name: ThemeName) => {
    setThemeName(name);
    localStorage.setItem(STORAGE_KEY, name);
  }, []);

  return (
    <ThemeContext.Provider value={{ themeName, theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used inside ThemeProvider');
  }
  return ctx;
}
